import { useCallback } from 'react';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  ariaLabel = "Page navigation" 
}) => {
  const handlePageChange = useCallback((page) => {
    // Validation stricte
    if (typeof page !== 'number' || page < 1 || page > totalPages) {
      console.warn(`Page invalide: ${page}, totalPages: ${totalPages}`);
      return;
    }
    
    // Callback sécurisé
    if (typeof onPageChange === 'function') {
      onPageChange(page);
    }
  }, [totalPages, onPageChange]);

  // Évite rendu si totalPages <= 1
  if (totalPages <= 1) return null;

  return (
    <nav aria-label={ariaLabel}>
      <ul className="pagination justify-content-center mb-0">
        {/* Bouton précédent */}
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Page précédente"
          >
            <i className="fa-solid fa-angle-left"></i>
          </button>
        </li>
        
        {/* Pages ellipsis pour > 5 pages */}
        {totalPages <= 5 ? (
          // Petites listes: toutes les pages
          [...Array(totalPages)].map((_, index) => (
            <li
              key={index + 1}
              className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(index + 1)}
                aria-label={`Page ${index + 1}`}
                aria-current={currentPage === index + 1 ? "page" : undefined}
              >
                {index + 1}
              </button>
            </li>
          ))
        ) : (
          // Grandes listes: ellipsis
          <>
            <li className={`page-item ${currentPage === 1 ? "active" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(1)}>
                1
              </button>
            </li>
            
            {currentPage > 3 && (
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            )}
            
            {Array.from({ length: 3 }, (_, i) => {
              const pageNum = currentPage - 1 + i;
              if (pageNum > 1 && pageNum < totalPages) {
                return (
                  <li key={pageNum} className={`page-item ${currentPage === pageNum ? "active" : ""}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              }
              return null;
            })}
            
            {currentPage < totalPages - 2 && (
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            )}
            
            <li className={`page-item ${currentPage === totalPages ? "active" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(totalPages)}>
                {totalPages}
              </button>
            </li>
          </>
        )}
        
        {/* Bouton suivant */}
        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Page suivante"
          >
            <i className="fa-solid fa-angle-right"></i>
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
